/**
 * StoreRegisterContainer
 * --
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import StoreRegisterPresenter from './StoreRegisterPresenter';
import { useNavigate } from 'react-router-dom';
import { LoadingPage } from 'components';
import MessageAlert from 'utils';
import validateCheck from 'utils/Validation';
import { useFileUpload } from 'hooks/FileHooks';
import { useDuplicateCheck } from 'hooks/AuthHooks';
import { useCreateAdmin } from 'hooks/AdminHooks';
import { useCreateShopInformationFile } from 'hooks/ShopInformationFileHooks';
import { useCreateShopInformation } from 'hooks/ShopInformationHooks';

const emailAddress = [
  {
    label: '직접입력',
    value: '',
  },
  { label: '네이버', value: 'naver.com' },
  { label: '구글', value: 'gmail.com' },
  { label: '다음', value: 'daum.net' },
  { label: '네이트', value: 'nate.com' },
  { label: '카카오메일', value: 'kakao.com' },
];

const StoreRegisterContainer = () => {
  /* ===== ROUTE ===== */
  const navigate = useNavigate();

  /* ===== Mutate ===== */
  // 중복검사
  const { duplicate } = useDuplicateCheck();
  // 관리자 생성
  const { createAdmin } = useCreateAdmin();
  // 쇼브매지 생성
  const { createShopInformation } = useCreateShopInformation();
  const { createShopInformationFile } = useCreateShopInformationFile();
  const { fileUpload } = useFileUpload();
  /* ===== PROPS ===== */
  /* ===== Ref ===== */
  const storeNameRef = useRef(null);
  const storeContentRef = useRef(null);
  const storeAddressRef = useRef(null);
  const accountRef = useRef(null);
  const passwordRef = useRef(null);
  const rePasswordRef = useRef(null);
  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const emailRef = useRef(null);

  /* ===== STATE ===== */
  // 매장 기본 정보
  const [inputValue, setInputValue] = React.useState({
    shop_information_name: '', // 매장 명
    shop_information_content: '', // 매장 설명
    shop_information_postcode: '', // 매장 우편번호
    shop_information_address: '', // 매장 주소
    shop_information_address_detail: '', // 매장 주소 상세
    application_status: 'APPROVE', // 입점신청여부
    correction_status: 'APPROVE',
    type: 'STORE', // 타입
  });
  // 매장 위도/경도
  const [coords, setCoords] = useState({
    latitude: 0, // 위도
    longitude: 0, // 경도
  });

  // 매장 정보
  const [informationValue, setInformationValue] = React.useState({
    store_num: '', // 매장 연락처
    business_num: '', // 사업자등록번호
    business_hour: '', // 영업시간
    store_owner: '',
    GROUP: true, // 단체 이용가능여부
    WIFI: true, // 무선 인터넷 사용 여부
    PARKING: true, // 주차 가능 여부
    RESTROOM: true, // 남녀 화장실 구분 여부
    PACKAGING: true, // 포장 가능 여부
    WHEELCHAIR: true, // 출입구 휠체어 이용 가능 여부
  });
  // 매장 파일 정보
  const [menuFiles, setMenuFiles] = React.useState([]);
  const [thumbnailFiles, setThumbnailFiles] = React.useState([]);
  const [storeFiles, setStoreFiles] = React.useState([]);
  const [licenseFiles, setLicenseFiles] = React.useState([]);
  const [etcFiles, setEtcFiles] = React.useState([]);

  // 관리자 정보
  const [adminInfo, setAdminInfo] = React.useState({
    admin_account: '', // 아이디
    password: '', // 패스워드
    re_password: '', // 비밀번호 확인
    admin_name: '', // 이름
    admin_phone: '', // 연락처
    admin_email: '', // 이메일
    admin_email_address: '', // 이메일 주소
    admin_level: 1, // 권한 레벨
    admin_privilege: 'CRUD', // 권한
    type: 'STORE_ADMIN', // 타입
  });
  // 이메일 주소 선택값
  const [address, setAddress] = useState('');
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 아이디 중복검사 여부
  // NOTE: DEFAULT: 중복검사 미실행 / FAIL: 중복됨 / SUCCESS: 중복안됨
  const [isChecked, setIsChecked] = React.useState('DEFAULT');

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = useCallback((t, v, o, callback = null) => {
    callback((prev) => {
      if (prev[t] === v) return prev;

      const newData = { ...prev, [t]: v };

      return newData;
    });
  }, []);

  /**
   * 좌표값 바인딩
   * --
   */
  const handleCoordChange = (lat, lng) => {
    setCoords({
      latitude: lat,
      longitude: lng,
    });
  };

  // 이메일 유효성 검사
  const validateEmailEmail = (emailAddress) => {
    const EmailPattern = /^[a-zA-Z]+(\.[a-zA-Z]{2,})+$/;
    // 정규식 검사 결과 반환
    return EmailPattern.test(emailAddress);
  };

  /**
   * 유효성 검사
   * --
   */
  const handleValidateCheck = () => {
    // NOTE: 매장 기본 정보 유효성 검사
    const {
      shop_information_name,
      shop_information_content,
      shop_information_address,
      toilet_information,
      wifi_information,
    } = inputValue;

    // 매장명 입력 여부 체크
    if (!shop_information_name) {
      MessageAlert.warning('매장명을 입력해주세요.');
      storeNameRef.current.focus();
      return false;
    }

    // 매장명 길이 체크
    if (shop_information_name.length > 20) {
      MessageAlert.warning('매장명은 20자 이하로 입력 가능합니다.');
      storeNameRef.current.focus();
      return false;
    }

    // 매장 설명 입력여부 체크
    if (!shop_information_content) {
      MessageAlert.warning('매장 설명을 입력해주세요.');
      storeContentRef.current.focus();
      return false;
    }

    // 매장 설명 길이 체크
    if (shop_information_content.length > 100) {
      MessageAlert.warning('매장 설명은 100자 이하로 입력 가능합니다.');
      storeContentRef.current.focus();
      return false;
    }

    // 매장 주소 입력 여부 체크
    if (!shop_information_address) {
      MessageAlert.warning('매장 주소를 입력해주세요.');
      storeAddressRef.current.focus();
      return false;
    }

    if (validateCheck('sanitize', toilet_information)) {
      MessageAlert.warning('화장실 정보에 특수기호는 넣을수 없습니다.');
      return false;
    }

    if (validateCheck('sanitize', wifi_information)) {
      MessageAlert.warning('wifi 정보에 특수기호는 넣을수 없습니다.');
      return false;
    }

    // NOTE: 매장 정보 유효성 검사
    const { store_num, business_num, business_hour, store_owner } =
      informationValue;

    // 대표자명 입력 여부 체크
    if (!store_owner) {
      MessageAlert.warning('대표자 명을 입력해주세요.');
      return false;
    }
    if (validateCheck('sanitize', store_owner)) {
      MessageAlert.warning('대표자 명은 이름 형식이여야 합니다.');
      return false;
    }

    // 매장번호 입력여부
    if (!store_num) {
      MessageAlert.warning('매장번호를 입력해주세요.');
      return false;
    }

    // 사업자등록번호 입력여부 체크
    if (!business_num) {
      MessageAlert.warning('사업자등록번호를 입력해주세요.');
      return false;
    }
    // 영업시간 입력여부 체크
    if (!business_hour) {
      MessageAlert.warning('영업시간을 입력해주세요.');
      return false;
    }
    if (validateCheck('sanitize', business_hour)) {
      MessageAlert.warning('영업시간에 특수기호는 넣을수 없습니다.');
      return false;
    }

    // NOTE: 관리자 정보 유효성 검사
    const {
      admin_name,
      admin_account,
      password,
      re_password,
      admin_phone,
      admin_email,
      admin_email_address,
    } = adminInfo;

    // 관리자명 입력여부 체크
    if (!admin_name) {
      MessageAlert.warning('관리자명을 입력해주세요.');
      nameRef.current.focus();
      return false;
    }

    // 관리자명 길이 체크
    if (admin_name.length > 5) {
      MessageAlert.warning('관리자명은 5자리 이하로 입력해주세요.');
      nameRef.current.focus();
      return false;
    }

    // 관리자 아이디 입력여부 체크
    if (!admin_account) {
      MessageAlert.warning('관리자 아이디를 입력해주세요.');
      accountRef.current.focus();
      return false;
    }

    // 관리자 아이디 유효성 검사
    if (!validateCheck('accountStore', admin_account)) {
      MessageAlert.warning(
        '아이디는 영문자가 하나 이상 포함되어야 하며, 특수문자는 _(언더바)만 사용할 수 있습니다.'
      );
      accountRef.current.focus();
      return false;
    }

    // 관리자 아이디 중복검사 여부 체크
    if (isChecked === 'DEFAULT') {
      MessageAlert.warning('아이디 중복검사를 진행해주세요.');
      accountRef.current.focus();
      return false;
    }

    if (isChecked === 'FAIL') {
      MessageAlert.warning('사용할 수 없는 아이디입니다.');
      accountRef.current.focus();
      return false;
    }

    // 비밀번호 입력여부 체크
    if (!password) {
      MessageAlert.warning('관리자 비밀번호를 입력해주세요.');
      passwordRef.current.focus();
      return false;
    }

    // 비밀번호 확인 입력여부 체크
    if (!re_password) {
      MessageAlert.warning('비밀번호 확인을 입력해주세요.');
      rePasswordRef.current.focus();
      return false;
    }

    // 비밀번호, 비밀번호 확인 일치 여부 체크
    if (password !== re_password) {
      MessageAlert.warning('비밀번호가 일치하지 않습니다.');
      passwordRef.current.focus();
      return false;
    }

    // 관리자 연락처 입력여부 체크
    if (!admin_phone) {
      MessageAlert.warning('관리자 연락처를 입력해주세요.');
      phoneRef.current.focus();
      return false;
    }

    // 관리자 연락처 길이 체크
    if (admin_phone.length < 9) {
      MessageAlert.warning('연락처를 정확히 입력해주세요.');
      phoneRef.current.focus();
      return false;
    }

    //이메일 유효성 검사
    if (!validateEmailEmail(admin_email_address)) {
      MessageAlert.warning('올바른 이메일을 입력해주세요.');
      return false;
    }

    // 이메일 입력여부 체크
    if (!admin_email || !admin_email_address) {
      MessageAlert.warning('관리자 이메일을 입력해주세요.');
      emailRef.current.focus();
      return false;
    }

    return true;
  };

  /**
   * 매장 등록 + 관리자 등록
   * --
   */
  const handleSubmit = async () => {
    // 유효성 검사
    if (!handleValidateCheck()) return;

    try {
      setIsLoading(true);

      // 매장 정보 데이터 정제
      const store = {
        ...inputValue,
        ...coords,
        store_information: {
          store_owner: informationValue?.store_owner, //대표자 명
          store_num: informationValue?.store_num, // 매장 연락처
          business_num: informationValue?.business_num, // 사업자등록번호
          business_hour: informationValue?.business_hour, // 영업시간
          services: {
            GROUP: informationValue?.GROUP,
            WIFI: informationValue?.WIFI,
            PARKING: informationValue?.PACKAGING,
            RESTROOM: informationValue?.RESTROOM,
            PACKAGING: informationValue?.PACKAGING,
            WHEELCHAIR: informationValue?.WHEELCHAIR,
          },
        },
      };

      // 매장 생성
      const { status: storeStatus, data: storeData } =
        await createShopInformation(store);

      if (storeStatus === 200) {
        const { shop_information_id } = storeData;

        // 관리자 정보 정제
        const { re_password, admin_email, admin_email_address, ...d } =
          adminInfo;
        const admin = {
          ...d,
          admin_email: `${admin_email}@${admin_email_address}`,
          shop_information_id: shop_information_id,
        };

        // 관리자 생성
        const { status: adminStatus } = await createAdmin(admin);

        if (adminStatus === 200) {
          if (
            thumbnailFiles?.length > 0 ||
            menuFiles?.length > 0 ||
            storeFiles?.length > 0 ||
            licenseFiles?.length > 0 ||
            etcFiles?.length > 0
          ) {
            // 파일 리스트 정제
            const files = [
              ...thumbnailFiles.map((file) => {
                file.category = 'THUMBNAIL';
                return file;
              }),
              ...menuFiles.map((file) => {
                file.category = 'MENU';
                return file;
              }),
              ...storeFiles.map((file) => {
                file.category = 'STORE';
                return file;
              }),
              ...licenseFiles.map((file) => {
                file.category = 'LICENSE';
                return file;
              }),
              ...etcFiles.map((file) => {
                file.category = 'ETC';
                return file;
              }),
            ];
            const shop_information_id = storeData.shop_information_id; // 생성된 매장 ID
            const fileSavePromises = files.map(async (file, index) => {
              const timestamp = Date.now();
              const fileExtension = file?.name?.split('.').pop();
              const baseName = file?.name?.replace(`.${fileExtension}`, '');
              const fileName = `jm_coffee/shop/${shop_information_id}/${baseName}_${timestamp}.${fileExtension}`;

              // 각 파일을 개별적으로 업로드
              const uploadResponse = await fileUpload({
                file,
                file_name: fileName,
              });

              if (uploadResponse) {
                // 파일 정보 저장
                const shopFileInfo = {
                  shop_information_id,
                  file_name: `${baseName}_${timestamp}.${fileExtension}`,
                  file_url: fileName,
                  type: file.category,
                };
                return createShopInformationFile(shopFileInfo);
              }
            });

            // 모든 파일 저장이 완료될 때까지 대기
            await Promise.all(fileSavePromises);
          }
          const confirm = window.confirm(
            `성공적으로 매장이 등록되었습니다.\n매장목록으로 이동하시겠습니까?`
          );

          if (confirm) navigate(`/store/list`);
          else window.location.reload();
          return;
        }

        MessageAlert.warning('관리자를 등록하는데 실패하였습니다.');
      } else {
        MessageAlert.warning('매장을 등록하는데 실패하였습니다.');
      }
    } catch (err) {
      console.error(`[CREATE STORE] ERROR : `, err);
      MessageAlert.error('매장을 등록하는데 오류가 발생하였습니다.');
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 뒤로가기
   */
  const handleCancel = () => {
    navigate(-1);
  };

  /**
   * 아이디 중복검사
   * --
   */
  const handleDuplication = async () => {
    const { admin_account } = adminInfo;

    // 관리자 아이디 입력여부 체크
    if (!admin_account) {
      MessageAlert.warning('관리자 아이디를 입력해주세요.');
      accountRef.current.focus();
      return false;
    }

    // 관리자 아이디 유효성 검사
    if (!validateCheck('accountStore', admin_account)) {
      MessageAlert.warning(
        '아이디는 영문자가 하나 이상 포함되어야 하며, 특수문자는 _(언더바)만 사용할 수 있습니다.'
      );
      accountRef.current.focus();
      return false;
    }

    try {
      // 아이디 중복검사
      const { data, status } = await duplicate({
        admin_account: admin_account,
        type: 'ADMIN',
      });

      if (status !== 200) {
        MessageAlert.warning(
          '아이디 중복검사를 진행하는데 실패하였습니다. 관리자에게 문의해주세요.'
        );
        return;
      }

      if (data) {
        MessageAlert.success('사용가능한 아이디입니다.');
        setIsChecked('SUCCESS');
      } else {
        MessageAlert.warning('사용할 수 없는 아이디입니다.');
        setIsChecked('FAIL');
      }
    } catch (err) {
      console.error(`[ACCOUNT CHECK] ERROR : `, err);
      MessageAlert.error(
        '아이디 중복검사를 진행하는데 실패하였습니다. 관리자에게 문의해주세요.'
      );
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    setIsChecked('DEFAULT');
  }, [inputValue.admin_account]);
  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && <LoadingPage text={'매장을 등록중입니다.'} />}
      <StoreRegisterPresenter
        // Ref
        storeNameRef={storeNameRef}
        storeContentRef={storeContentRef}
        storeAddressRef={storeAddressRef}
        accountRef={accountRef}
        passwordRef={passwordRef}
        rePasswordRef={rePasswordRef}
        nameRef={nameRef}
        phoneRef={phoneRef}
        emailRef={emailRef}
        // State
        inputValue={inputValue}
        informationValue={informationValue}
        adminInfo={adminInfo}
        emailAddress={emailAddress}
        address={address}
        files={{
          menu: menuFiles,
          thumbnail: thumbnailFiles,
          store: storeFiles,
          license: licenseFiles,
          etc: etcFiles,
        }}
        setInputValue={setInputValue}
        setInformationValue={setInformationValue}
        setAdminInfo={setAdminInfo}
        onChange={handleChange}
        onCoordChange={handleCoordChange}
        setAddress={setAddress}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onDuplication={handleDuplication}
        onThumbnailFileChange={setThumbnailFiles}
        onMenuFileChange={setMenuFiles}
        onStoreFileChange={setStoreFiles}
        onLicenseFileChange={setLicenseFiles}
        onEtcFileChange={setEtcFiles}
      />
    </>
  );
};

export default StoreRegisterContainer;
